import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * Adresy serwerów wchodzą do wersji przy budowaniu (VITE_*). Domyślne wartości wskazują
 * na produkcję, więc obraz zbudowany bez parametrów jest tym produkcyjnym.
 */
export const config = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'https://api.firmeto.pl',
  authIssuer: import.meta.env.VITE_AUTH_ISSUER ?? 'https://auth.firmeto.pl/realms/firmeto',
  clientId: 'firmeto-web',
};

/**
 * Tokeny trzymamy **w pamięci karty**, a nie w localStorage: skrypt wstrzyknięty w stronę
 * nie ma ich skąd wykraść, a odświeżenie strony odzyskuje sesję po cichu z Keycloaka
 * (sesja SSO w ciasteczku). Przy zamknięciu karty sesja w przeglądarce znika.
 */
class InMemoryStorage implements Storage {
  private data = new Map<string, string>();

  get length() {
    return this.data.size;
  }

  clear() {
    this.data.clear();
  }

  getItem(key: string) {
    return this.data.get(key) ?? null;
  }

  key(index: number) {
    return [...this.data.keys()][index] ?? null;
  }

  removeItem(key: string) {
    this.data.delete(key);
  }

  setItem(key: string, value: string) {
    this.data.set(key, value);
  }
}

export const userManager = new UserManager({
  authority: config.authIssuer,
  client_id: config.clientId,
  redirect_uri: `${window.location.origin}/logowanie/powrot`,
  post_logout_redirect_uri: window.location.origin,
  silent_redirect_uri: `${window.location.origin}/logowanie/odnowienie`,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 5,
  // Stan logowania (w tym weryfikator PKCE) musi przetrwać przekierowanie do Keycloaka
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  userStore: new WebStorageStateStore({ store: new InMemoryStorage() }),
});

/**
 * Ślad po tym, że ta przeglądarka kiedyś się logowała. Bez niego każdy gość czekałby kilka
 * sekund na ciche odzyskanie sesji, które i tak nie ma prawa się udać. Sam znacznik niczego
 * nie uwierzytelnia — prawdziwa sesja jest w ciasteczku Keycloaka.
 */
const SESSION_HINT = 'firmeto-logowal-sie';

/**
 * Ślad próby odzyskania sesji pełnym przekierowaniem — jeden na kartę.
 *
 * Bez niego nieudana próba wracałaby na tę samą stronę i odpalała kolejną, w kółko.
 */
const REDIRECT_ATTEMPT = 'firmeto-proba-odzyskania';

/**
 * Przejście do logowania w Keycloaku. Adres podany po rejestracji trafia do `login_hint`,
 * żeby po założeniu konta nie trzeba było wpisywać go drugi raz.
 *
 * Razem z żądaniem jedzie bieżący adres w aplikacji. Keycloak oddaje go nietknięty, więc po
 * zalogowaniu wracamy tam, skąd się wyszło — link do faktury przysłany księgowej prowadzi do
 * faktury, a nie na listę firm.
 */
export async function signIn(loginHint?: string): Promise<void> {
  await userManager.signinRedirect({
    ...(loginHint ? { login_hint: loginHint } : {}),
    state: { path: window.location.pathname },
  });
}

export async function signOut(): Promise<void> {
  window.localStorage.removeItem(SESSION_HINT);
  await userManager.signoutRedirect();
}

export function rememberSignedIn(): void {
  window.localStorage.setItem(SESSION_HINT, '1');
  window.sessionStorage.removeItem(REDIRECT_ATTEMPT);
}

export function forgetSignedIn(): void {
  window.localStorage.removeItem(SESSION_HINT);
}

/**
 * Błędy, które naprawdę znaczą „nie ma sesji w Keycloaku".
 *
 * Reszta — zerwana sieć, wolna odpowiedź, chwilowa awaria — nie jest dowodem na wylogowanie
 * i nie może kasować znacznika. Kasowanie go przy każdym niepowodzeniu kończyło się tak,
 * że po jednej wolnej odpowiedzi użytkownik z żywą sesją lądował na ekranie startowym
 * i musiał klikać „Mam już konto", choć nadal był zalogowany.
 */
export function meansNoSession(failure: unknown): boolean {
  const code = (failure as { error?: unknown })?.error;
  return (
    typeof code === 'string' &&
    ['login_required', 'interaction_required', 'consent_required', 'account_selection_required'].includes(code)
  );
}

/**
 * Sesja odzyskiwana bez pytania użytkownika; null, gdy trzeba się zalogować.
 *
 * Próbę podejmujemy zawsze, a nie tylko przy zapamiętanym znaczniku: gość i tak dostaje
 * odpowiedź po jednym obiegu (Keycloak od razu odsyła `login_required`), a osoba z żywą sesją
 * nie utyka na ekranie startowym przez znacznik, którego akurat nie ma — bo logowała się
 * w innej przeglądarce, wyczyściła dane albo trafiła kiedyś na chwilową awarię.
 */
export async function restoreSession(): Promise<User | null> {
  const current = await userManager.getUser();
  if (current && !current.expired) return current;
  try {
    const user = await userManager.signinSilent();
    if (user) rememberSignedIn();
    return user;
  } catch (failure) {
    if (meansNoSession(failure)) {
      forgetSignedIn();
      return null;
    }

    /*
     * Ramka nie odpowiedziała — a to nie znaczy, że sesji nie ma.
     *
     * Ciche odnowienie ładuje naszą stronę w ramce i przewraca się o wszystko: nagłówek
     * zabraniający oprawiania, zaostrzone reguły ciasteczek, zerwaną sieć. Pełne przekierowanie
     * z `prompt=none` nie używa ramki i zawsze niesie ciasteczko sesji, więc jest tu ostatnią
     * deską ratunku. Próbujemy raz na kartę i tylko wtedy, gdy ta przeglądarka już się logowała.
     */
    if (wasSignedInBefore() && !window.sessionStorage.getItem(REDIRECT_ATTEMPT)) {
      window.sessionStorage.setItem(REDIRECT_ATTEMPT, '1');
      await userManager.signinRedirect({ prompt: 'none', state: { path: window.location.pathname } });
    }
    return null;
  }
}

/** Czy ta przeglądarka logowała się już kiedyś — po tym poznajemy, czy warto czekać na sesję. */
export function wasSignedInBefore(): boolean {
  return window.localStorage.getItem(SESSION_HINT) !== null;
}
